import { CmsEditorialText } from '@/components/cms/CmsEditorialText';
/**
 * Podstrona „Dla firm" — florystyka i kwiaty dla biznesu.
 * /dla-firm
 */
import type { MouseEvent } from 'react';
import type { LucideIcon } from 'lucide-react';
import { useInView } from '@/lib/useInView';
import { Link } from 'wouter';
import {
  ArrowRight, Building2, UtensilsCrossed, Sparkles, Store,
  CalendarDays, Home, Leaf, Flower2, Gift, RefreshCw, Phone,
  CheckCircle2,
} from 'lucide-react';
import { Footer } from '@/components/sections/Footer';
import { SectionLabel } from '@/components/ui/SectionLabel';
import { useSeo } from '@/lib/useSeo';
import { PAGE_SEO } from '@/data/pageSeoConfig';
import { PageBreadcrumb } from '@/components/sections/PageBreadcrumb';
import { usePrerenderReady } from '@/hooks/usePrerenderReady';
import { DLA_FIRM_CONTENT } from '@/data/dlaFirmContent';

/* ── Tokens ──────────────────────────────────────────────── */
const GOLD   = '#e05590';
const DARK   = '#2a1f14';
const MUTED  = 'rgba(42,31,20,0.50)';
const LINE   = 'rgba(210,60,120,0.18)';
const CREAM  = '#fff';
const CREAM2 = '#fff';
const CREAM3 = '#fff';
const BROWN  = '#5a3e28';

/* ── Resolves iconKey strings from DLA_FIRM_CONTENT to Lucide components —
   same pattern as CategoryNav.tsx/Footer.tsx (icon reference stays in code,
   config only carries the string key). ── */
const ICON_MAP: Record<string, LucideIcon> = {
  building2: Building2, utensils: UtensilsCrossed, sparkles: Sparkles,
  store: Store, calendar: CalendarDays, home: Home,
  leaf: Leaf, flower: Flower2, gift: Gift, refresh: RefreshCw,
};

const btnPrimary: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 10,
  fontFamily: 'DM Sans, sans-serif', fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase',
  color: '#fff', background: DARK, border: 'none', padding: '14px 32px', cursor: 'pointer', textDecoration: 'none',
};
const btnOutline: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 10,
  fontFamily: 'DM Sans, sans-serif', fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase',
  color: DARK, background: 'transparent', border: `1px solid rgba(42,31,20,0.28)`, padding: '14px 32px', cursor: 'pointer', textDecoration: 'none',
};

/* ── 1. HERO ─────────────────────────────────────────────── */
function Hero() {
  return (
    <section style={{ position: 'relative', minHeight: 'calc(52dvh - 45px)', display: 'flex', alignItems: 'center', background: '#fff', overflow: 'hidden' }}>
      <PageBreadcrumb crumbs={[{ label: 'Dla firm' }]} />

      <div style={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: 1370, margin: '0 auto', padding: 'max(clamp(24px, 3vw, 48px), calc(var(--page-breadcrumb-height, 44px) + 12px)) var(--page-gutter) clamp(24px, 3vw, 48px)' }}>
        <div style={{ maxWidth: 720, margin: '0 auto', textAlign: 'center' }}>


          <p className="bk-fade-up bk-d4"
            style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 'clamp(1.1rem, 2vw, 1.4rem)', fontWeight: 400, color: BROWN, marginBottom: 16, fontStyle: 'italic' }}>
            <CmsEditorialText id="information.hero.tagline" value={DLA_FIRM_CONTENT.hero.tagline} section="Wstęp" label="Nadtytuł" />
          </p>

          <h1 className="bk-fade-up bk-d5"
            style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 'clamp(2.2rem, 5.9vw, 5.4rem)', fontWeight: 400, lineHeight: 1.0, color: DARK, margin: '0 0 28px' }}>
            <CmsEditorialText id="information.hero.headingLine1" value={DLA_FIRM_CONTENT.hero.headingLine1} section="Wstęp" label="Nagłówek — pierwsza linia" /><br /><em style={{ color: GOLD, fontStyle: 'italic' }}><CmsEditorialText id="information.hero.headingAccent" value={DLA_FIRM_CONTENT.hero.headingAccent} section="Wstęp" label="Wyróżniona część nagłówka" /></em>
          </h1>

          <p className="bk-fade-in bk-d6"
            style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 'clamp(14px, 1.6vw, 17px)', color: BROWN, lineHeight: 1.85, maxWidth: 540, margin: '0 auto 44px' }}>
            <CmsEditorialText id="information.hero.body" value={DLA_FIRM_CONTENT.hero.body} section="Wstęp" label="Opis" multiline />
          </p>

          <div className="bk-fade-up" style={{ display: 'flex', gap: 16, flexWrap: 'wrap', justifyContent: 'center', animationDelay: '0.75s' }}>
            <a href="tel:+48605030728"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 10, fontFamily: 'DM Sans, sans-serif', fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#fff', textDecoration: 'none', background: DARK, padding: '15px 32px', transition: 'opacity 0.2s', cursor: 'pointer' }}
              onMouseEnter={e => ((e.currentTarget as HTMLAnchorElement).style.opacity = '0.85')}
              onMouseLeave={e => ((e.currentTarget as HTMLAnchorElement).style.opacity = '1')}
            >
              <Phone size={13} />
              <CmsEditorialText id="information.hero.ctaPhoneLabel" value={DLA_FIRM_CONTENT.hero.ctaPhoneLabel} section="Wstęp" label="Tekst przycisku" />
            </a>
            <Link href="/kontakt/"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 10, fontFamily: 'DM Sans, sans-serif', fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', color: DARK, textDecoration: 'none', border: `1px solid ${GOLD}88`, padding: '15px 32px', transition: 'border-color 0.2s', cursor: 'pointer' }}
              onMouseEnter={(e: MouseEvent<HTMLAnchorElement>) => (e.currentTarget.style.borderColor = GOLD)}
              onMouseLeave={(e: MouseEvent<HTMLAnchorElement>) => (e.currentTarget.style.borderColor = `${GOLD}88`)}
            >
              <CmsEditorialText id="information.hero.ctaOfferLabel" value={DLA_FIRM_CONTENT.hero.ctaOfferLabel} section="Wstęp" label="Tekst przycisku" />
              <ArrowRight size={13} />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── 2. DLA KOGO ─────────────────────────────────────────── */
function ForWhoSection() {
  const { ref, inView } = useInView<HTMLElement>();
  return (
    <section ref={ref} style={{ background: '#fff', padding: 'clamp(64px,8vw,104px) 0' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 clamp(24px,5vw,64px)' }}>
        <div style={{ opacity: inView ? undefined : 0, textAlign: 'center', marginBottom: 'clamp(40px,5vw,56px)' }} className={inView ? 'bk-fade-up' : undefined}>
          <SectionLabel text={DLA_FIRM_CONTENT.forWho.eyebrow} />
          <h2 style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 'clamp(2rem,3.5vw,3.2rem)', fontWeight: 400, color: DARK, margin: 0, lineHeight: 1.1 }}>
            <CmsEditorialText id="information.forWho.heading" value={DLA_FIRM_CONTENT.forWho.heading} section="Odbiorcy" label="Nagłówek" />
          </h2>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%,320px),1fr))', gap: 1, background: LINE }}>
          {DLA_FIRM_CONTENT.forWho.clients.map((c, i) => {
            const Icon = ICON_MAP[c.iconKey];
            return (
              <div key={c.title}
                style={{ background: '#fff', padding: 'clamp(28px,3vw,40px)', borderTop: `2px solid ${GOLD}44`, opacity: inView ? undefined : 0, animationDelay: `${i * 0.06}s` }}
                className={inView ? 'bk-fade-up' : undefined}
              >
                <div style={{ width: 44, height: 44, borderRadius: '50%', background: `${GOLD}10`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 18 }}>
                  <Icon size={17} color={GOLD} />
                </div>
                <h3 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(17px,1.8vw,21px)', fontWeight: 400, color: DARK, margin: '0 0 10px', lineHeight: 1.25 }}>
                  <CmsEditorialText id={`information.forWho.clients.${i}.title`} value={c.title} section="Odbiorcy" label={`Element ${i + 1}: tytuł`} />
                </h3>
                <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 13.5, color: MUTED, lineHeight: 1.75, margin: 0 }}>
                  <CmsEditorialText id={`information.forWho.clients.${i}.desc`} value={c.desc} section="Odbiorcy" label={`Element ${i + 1}: opis`} multiline />
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ── 3. CO OFERUJEMY ─────────────────────────────────────── */
function OfferSection() {
  const { ref, inView } = useInView<HTMLElement>();
  return (
    <section ref={ref} style={{ background: '#fff', padding: 'clamp(64px,8vw,104px) 0' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 clamp(24px,5vw,64px)' }}>
        <div style={{ opacity: inView ? undefined : 0, textAlign: 'center', marginBottom: 'clamp(40px,5vw,56px)' }} className={inView ? 'bk-fade-up' : undefined}>
          <SectionLabel text={DLA_FIRM_CONTENT.offer.eyebrow} />
          <h2 style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 'clamp(2rem,3.5vw,3.2rem)', fontWeight: 400, color: DARK, margin: 0, lineHeight: 1.1 }}>
            <CmsEditorialText id="information.offer.heading" value={DLA_FIRM_CONTENT.offer.heading} section="Oferta" label="Nagłówek" />
          </h2>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,340px),1fr))', gap: 'clamp(16px,2vw,24px)' }}>
          {DLA_FIRM_CONTENT.offer.items.map((o, i) => {
            const Icon = ICON_MAP[o.iconKey];
            return (
              <div key={o.title}
                style={{
                  background: '#fff', padding: 'clamp(28px,3vw,40px)',
                  border: `1px solid ${LINE}`, position: 'relative', overflow: 'hidden',
                  opacity: inView ? undefined : 0, animationDelay: `${i * 0.07}s`,
                }}
                className={inView ? 'bk-fade-up' : undefined}
              >
                {/* Tag pill */}
                <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 9, letterSpacing: '0.24em', textTransform: 'uppercase', color: GOLD, background: `${GOLD}12`, padding: '4px 10px', display: 'inline-block', marginBottom: 20 }}>
                  <CmsEditorialText id={`information.offer.items.${i}.tag`} value={o.tag} section="Oferta" label={`Element ${i + 1}: nadtytuł`} />
                </span>
                <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                  <div style={{ width: 40, height: 40, borderRadius: '50%', background: `${GOLD}10`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 2 }}>
                    <Icon size={16} color={GOLD} />
                  </div>
                  <div>
                    <h3 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(17px,1.8vw,21px)', fontWeight: 400, color: DARK, margin: '0 0 10px', lineHeight: 1.25 }}>
                      <CmsEditorialText id={`information.offer.items.${i}.title`} value={o.title} section="Oferta" label={`Element ${i + 1}: tytuł`} />
                    </h3>
                    <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 13.5, color: MUTED, lineHeight: 1.75, margin: 0 }}>
                      <CmsEditorialText id={`information.offer.items.${i}.desc`} value={o.desc} section="Oferta" label={`Element ${i + 1}: opis`} multiline />
                    </p>
                  </div>
                </div>
                {/* Decorative corner dot */}

              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ── 4. REGULARNA OPIEKA ─────────────────────────────────── */
function RegularCareSection() {
  const { ref, inView } = useInView<HTMLElement>();
  return (
    <section ref={ref} style={{ background: '#fff', padding: 'clamp(64px,8vw,104px) 0', position: 'relative', overflow: 'hidden' }}>
      {/* Subtle decorative element */}

      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 clamp(24px,5vw,64px)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,440px),1fr))', gap: 'clamp(40px,6vw,80px)', alignItems: 'center' }}>

          <div style={{ opacity: inView ? undefined : 0 }} className={inView ? 'bk-fade-up' : undefined}>
            <SectionLabel text={DLA_FIRM_CONTENT.regularCare.eyebrow} />
            <h2 style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 'clamp(2rem,3.5vw,3.2rem)', fontWeight: 400, color: DARK, margin: '0 0 20px', lineHeight: 1.1 }}>
              <CmsEditorialText id="information.regularCare.headingLine1" value={DLA_FIRM_CONTENT.regularCare.headingLine1} section="Stała współpraca" label="Nagłówek — pierwsza linia" /><br />
              <em style={{ color: GOLD, fontStyle: 'italic' }}><CmsEditorialText id="information.regularCare.headingAccent" value={DLA_FIRM_CONTENT.regularCare.headingAccent} section="Stała współpraca" label="Wyróżniona część nagłówka" /></em>
            </h2>
            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 'clamp(14px,1.4vw,15px)', color: MUTED, lineHeight: 1.9, margin: '0 0 32px', maxWidth: 480 }}>
              <CmsEditorialText id="information.regularCare.body" value={DLA_FIRM_CONTENT.regularCare.body} section="Stała współpraca" label="Opis" multiline />
            </p>
            <Link href="/kontakt/" style={btnPrimary}>
              <CmsEditorialText id="information.regularCare.ctaLabel" value={DLA_FIRM_CONTENT.regularCare.ctaLabel} section="Stała współpraca" label="Tekst przycisku" /> <ArrowRight size={14} />
            </Link>
          </div>

          {/* Visual checklist */}
          <div style={{ opacity: inView ? undefined : 0, animationDelay: '0.12s' }} className={inView ? 'bk-fade-up' : undefined}>
            {DLA_FIRM_CONTENT.regularCare.checklist.map((item, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 14, padding: 'clamp(14px,2vw,20px) 0', borderBottom: `1px solid ${LINE}` }}>
                <CheckCircle2 size={16} color={GOLD} style={{ flexShrink: 0, marginTop: 2 }} />
                <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 14, color: DARK, margin: 0, lineHeight: 1.6 }}>
                  <CmsEditorialText id={`information.regularCare.checklist.${i}.text`} value={item} section="Stała współpraca" label={`Element ${i + 1}: tekst`} multiline />
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── 5. DLACZEGO BUKIETOWA ───────────────────────────────── */
function WhySection() {
  const { ref, inView } = useInView<HTMLElement>();
  return (
    <section ref={ref} style={{ background: '#fff', padding: 'clamp(64px,8vw,104px) 0' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 clamp(24px,5vw,64px)' }}>
        <div style={{ opacity: inView ? undefined : 0, textAlign: 'center', marginBottom: 'clamp(40px,5vw,56px)' }} className={inView ? 'bk-fade-up' : undefined}>
          <SectionLabel text={DLA_FIRM_CONTENT.why.eyebrow} />
          <h2 style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 'clamp(2rem,3.5vw,3.2rem)', fontWeight: 400, color: DARK, margin: 0, lineHeight: 1.1 }}>
            <CmsEditorialText id="information.why.heading" value={DLA_FIRM_CONTENT.why.heading} section="Dlaczego Bukietowa" label="Nagłówek" />
          </h2>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,260px),1fr))', gap: 1, background: LINE }}>
          {DLA_FIRM_CONTENT.why.items.map((w, i) => (
            <div key={w.title}
              style={{ background: '#fff', padding: 'clamp(28px,3vw,44px)', borderTop: `2px solid ${GOLD}`, opacity: inView ? undefined : 0, animationDelay: `${i * 0.07}s` }}
              className={inView ? 'bk-fade-up' : undefined}
            >
              {/* Roman numeral */}
              <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 38, fontWeight: 400, color: `${GOLD}30`, margin: '0 0 18px', lineHeight: 1 }}>
                {['I','II','III','IV'][i]}
              </p>
              <h3 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(18px,1.8vw,22px)', fontWeight: 400, color: DARK, margin: '0 0 12px', lineHeight: 1.2 }}>
                <CmsEditorialText id={`information.why.items.${i}.title`} value={w.title} section="Dlaczego Bukietowa" label={`Element ${i + 1}: tytuł`} />
              </h3>
              <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 13.5, color: MUTED, lineHeight: 1.75, margin: 0 }}>
                <CmsEditorialText id={`information.why.items.${i}.desc`} value={w.desc} section="Dlaczego Bukietowa" label={`Element ${i + 1}: opis`} multiline />
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── 6. JAK ZACZĄĆ ───────────────────────────────────────── */
function HowToStartSection() {
  const { ref, inView } = useInView<HTMLElement>();
  const steps = DLA_FIRM_CONTENT.howToStart.steps;
  return (
    <section ref={ref} style={{ background: '#fff', padding: 'clamp(64px,8vw,104px) 0' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 clamp(24px,5vw,64px)' }}>
        <div style={{ opacity: inView ? undefined : 0, textAlign: 'center', marginBottom: 'clamp(48px,6vw,64px)' }} className={inView ? 'bk-fade-up' : undefined}>
          <SectionLabel text={DLA_FIRM_CONTENT.howToStart.eyebrow} />
          <h2 style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 'clamp(2rem,3.5vw,3.2rem)', fontWeight: 400, color: DARK, margin: 0, lineHeight: 1.1 }}>
            <CmsEditorialText id="information.howToStart.heading" value={DLA_FIRM_CONTENT.howToStart.heading} section="Jak zacząć" label="Nagłówek" />
          </h2>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,300px),1fr))', gap: 'clamp(24px,4vw,40px)', position: 'relative' }}>
          {steps.map((s, i) => (
            <div key={s.num} style={{ position: 'relative', opacity: inView ? undefined : 0, animationDelay: `${i * 0.1}s` }} className={inView ? 'bk-fade-up' : undefined}>
              {/* Connector line (desktop) */}
              <div style={{ padding: 'clamp(28px,3vw,40px)', background: '#fff', border: `1px solid ${LINE}`, height: '100%', boxSizing: 'border-box' }}>
                <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 52, fontWeight: 400, color: `${GOLD}22`, margin: '0 0 16px', lineHeight: 1 }}>
                  {s.num}
                </p>

                <h3 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(20px,2vw,26px)', fontWeight: 400, color: DARK, margin: '0 0 14px', lineHeight: 1.2 }}>
                  <CmsEditorialText id={`information.howToStart.steps.${i}.title`} value={s.title} section="Jak zacząć" label={`Element ${i + 1}: tytuł`} />
                </h3>
                <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 14, color: MUTED, lineHeight: 1.8, margin: 0 }}>
                  <CmsEditorialText id={`information.howToStart.steps.${i}.desc`} value={s.desc} section="Jak zacząć" label={`Element ${i + 1}: opis`} multiline />
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── 7. CTA końcowe ──────────────────────────────────────── */
function CtaSection() {
  const { ref, inView } = useInView<HTMLElement>();
  return (
    <section ref={ref} style={{ background: '#fff', padding: 'clamp(72px,9vw,112px) 0', position: 'relative', overflow: 'hidden', borderTop: `1px solid ${LINE}` }}>
      {/* Decorative radial glow */}

      <div style={{ maxWidth: 720, margin: '0 auto', padding: '0 clamp(24px,5vw,64px)', textAlign: 'center', position: 'relative', zIndex: 1 }}>
        <div style={{ opacity: inView ? undefined : 0 }} className={inView ? 'bk-fade-up' : undefined}>
          <SectionLabel text={DLA_FIRM_CONTENT.cta.eyebrow} />
          <h2 style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 'clamp(2.2rem,4vw,3.8rem)', fontWeight: 400, lineHeight: 1.08, color: DARK, margin: '0 0 16px' }}>
            <CmsEditorialText id="information.cta.headingLine1" value={DLA_FIRM_CONTENT.cta.headingLine1} section="Zachęta do kontaktu" label="Nagłówek — pierwsza linia" /><br />
            <em style={{ color: GOLD, fontStyle: 'italic' }}><CmsEditorialText id="information.cta.headingAccent" value={DLA_FIRM_CONTENT.cta.headingAccent} section="Zachęta do kontaktu" label="Wyróżniona część nagłówka" /></em>
          </h2>
          <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 'clamp(14px,1.4vw,15.5px)', color: MUTED, lineHeight: 1.85, margin: '0 0 40px' }}>
            <CmsEditorialText id="information.cta.body" value={DLA_FIRM_CONTENT.cta.body} section="Zachęta do kontaktu" label="Opis" multiline />
          </p>
          <Link href="/kontakt/" style={{ ...btnPrimary, padding: '16px 40px' }}>
            <CmsEditorialText id="information.cta.ctaLabel" value={DLA_FIRM_CONTENT.cta.ctaLabel} section="Zachęta do kontaktu" label="Tekst przycisku" /> <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ── Page ────────────────────────────────────────────────── */
export default function DlaFirmPage() {
  usePrerenderReady();
  useSeo(PAGE_SEO.dlaFirm);
  return (
    <div style={{ background: '#fff', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <main style={{ flex: 1 }}>
        <Hero />
        <ForWhoSection />
        <OfferSection />
        <RegularCareSection />
        <WhySection />
        <HowToStartSection />
        <CtaSection />
      </main>
      <Footer />
    </div>
  );
}
